import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  Grid,
  Typography,
} from "@material-ui/core";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { AppBarButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Error } from "../common/Error";
import { ApproveQualification } from "../../domain/ApproveQualification";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { approveQualification } from "../../redux/actions";
import { QualApprovalStatus, Qualification } from "../../domain/Qualification";
import { fieldLabels } from "../sections/fieldLabels";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { entitlementFrameworkDesignationService } from "../../services";
import { Select } from "@ofqual/portal-components/components/Select";

type ApproveButtonAndDialogProps = {
  item: Qualification;
};

export const ApproveButtonAndDialog: React.FC<ApproveButtonAndDialogProps> = (
  props
) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <AppBarButton text="Approve" data-test="approve-button" onClick={() => setIsOpen(true)} />
      {isOpen && <ApproveDialog {...props} onClose={() => setIsOpen(false)} />}
    </>
  );
};

type ApproveDialogProps = {
  item: Qualification;
  onClose: () => void;
};

const ApproveDialog: React.FC<ApproveDialogProps> = ({ item, onClose }) => {
  const dispatch = useDispatch();
  const { control, errors, handleSubmit } = useForm({
    defaultValues: { ...item, response: "" },
    shouldUnregister: false,
  });

  const onSubmit = (model: ApproveQualification) => {
    dispatch(approveQualification(item.id, model));
    onClose();
  };

  const isCceaApproval =
    item.approvalStatus === QualApprovalStatus.AwaitingCceaApproval;

  const [entitlementFrameworkLookups] = useLookup(
    {
      service: entitlementFrameworkDesignationService,
      id: "id",
      label: "description",
    }
  );

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Approve Qualification</DialogTitle>
        <Divider component="div" />
        <DialogContent>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body1">
                  Please complete the following fields and confirm you wish to
                  approve this qualification
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="response"
                  rules={{ required: true }}
                  as={
                    <TextField
                      label="Response"
                      data-test="response"
                      required
                      multiline
                      rows={5}
                    />
                  }
                />
                {errors.response && <Error>This field is required</Error>}
              </Grid>
              {isCceaApproval ? (
                <>
                  <Grid item xs={12}>
                    <FormLabel component="legend">
                      {fieldLabels.approvedForDELFundedProgramme}
                    </FormLabel>
                    <Controller
                      control={control}
                      name="approvedForDELFundedProgramme"
                      render={(props) => (
                        <Checkbox
                          onChange={(e: any) => {
                            props.onChange(e.target.checked);
                          }}
                          data-test="approved-delf-checkbox"
                          checked={props.value}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="entitlementFrameworkDesignation"
                      as={
                        <Select
                          label={fieldLabels.entitlementFrameworkDesignation}
                          data-test="qualifications entitlement-methods"
                          // disabled={disabled}
                          // lookupTracker={lookupTracker}
                        >
                          {entitlementFrameworkLookups}
                        </Select>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="gcseSizeEquivalence"
                      as={
                        <TextField
                          label={fieldLabels.gcseSizeEquivalence}
                          data-test="gcse-size-equivalence"
                          fullWidth
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="gceSizeEquivalence"
                      as={
                        <TextField
                          label={fieldLabels.gceSizeEquivalence}
                          data-test="gce-size-equivalence"
                          fullWidth
                        />
                      }
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Controller
                    name="embargoDate"
                    control={control}
                    as={<DatePicker dataTest="approve-embargo-date" label="Embargo date (dd/mm/yyyy)" />}
                  />
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} data-test="approve-cancel-button" color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} data-test="approve-confirm-button" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
